import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { masterApi, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { Plus, Pencil, Trash2, Save, Users2, UserCheck, Car } from "lucide-react";

const inputCls = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

const fmtNum = (v) => Number(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 });

export const CATEGORIA_GRUPO = { normal: "Cliente Normal", app_driver: "Motorista de App" };
export const REGRAS_GRUPO = {
  somente_grupo: { label: "Só o desconto do grupo", desc: "O cliente recebe apenas o desconto do grupo; as promoções do sistema não são oferecidas e não acumula cashback novo (saldo já ganho continua resgatável)." },
  cumulativo: { label: "Grupo + descontos do sistema", desc: "O cliente recebe os descontos ativos no sistema e o desconto do grupo é somado a eles." },
  somente_sistema: { label: "Só os descontos do sistema", desc: "O cliente recebe apenas os descontos ativos no sistema, sem desconto adicional do grupo." },
};

export function descricaoDescontoGrupo(g) {
  if (!g) return "";
  if (g.regra_desconto === "somente_sistema" || !g.tipo_desconto) return "Sem desconto próprio";
  if (g.tipo_desconto === "percentual") return `${fmtNum(g.valor)}% no preço do litro`;
  const reais = (Number(g.valor || 0) / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  return `${fmtNum(g.valor)}¢ por litro (${reais}/L)`;
}

export default function MasterGruposConsumo() {
  const [grupos, setGrupos] = useState([]);
  const [vinculoFrentista, setVinculoFrentista] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [savingFlag, setSavingFlag] = useState(false);

  const load = async () => {
    try {
      const { data } = await masterApi.get("/grupos-consumo");
      setGrupos(data.grupos || []);
      setVinculoFrentista(data.vinculo_frentista_ativo !== false);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggleVinculo = async (next) => {
    setSavingFlag(true);
    try {
      await masterApi.put("/master/config", { grupo_consumo_frentista_ativo: next });
      setVinculoFrentista(next);
      modal.success(next
        ? "O frentista pode vincular o cliente a um grupo de consumo no cadastro."
        : "Vinculação pelo frentista desativada. Só o Master vincula clientes aos grupos.");
    } catch (e) { modal.error(apiError(e)); }
    finally { setSavingFlag(false); }
  };

  const toggleAtivo = async (g) => {
    try {
      await masterApi.put(`/grupos-consumo/${g.id}`, {
        nome: g.nome, categoria: g.categoria, regra_desconto: g.regra_desconto,
        tipo_desconto: g.tipo_desconto, valor: g.valor, ativo: !g.ativo,
      });
      load();
    } catch (e) { modal.error(apiError(e)); }
  };

  const del = async (g) => {
    const n = g.clientes_vinculados || 0;
    const ok = await modal.confirm({
      title: "Excluir grupo de consumo",
      message: n
        ? `O grupo "${g.nome}" será excluído e ${n} cliente${n > 1 ? "s ficarão" : " ficará"} sem grupo. Os cadastros dos clientes continuam intactos.`
        : `O grupo "${g.nome}" será excluído.`,
      confirmText: "Excluir",
      tone: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await masterApi.delete(`/grupos-consumo/${g.id}`);
      modal.success(data.clientes_desvinculados
        ? `Grupo excluído. ${data.clientes_desvinculados} cliente(s) desvinculado(s).`
        : "Grupo excluído.");
      load();
    } catch (e) { modal.error(apiError(e)); }
  };

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-grupos-consumo">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="chip mb-1.5 text-[10px] py-0.5">Cadastro</div>
          <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Grupos de Consumo</h1>
          <p className="text-[12px] text-neutral-500 mt-1">
            Subgrupos de Cliente Normal ou Motorista de App. A regra de cada grupo define como o desconto dele combina com os descontos do sistema.
          </p>
        </div>
        <button data-testid="novo-grupo-btn" onClick={() => { setEditing(null); setOpen(true); }}
                className="btn-action btn-novo">
          <Plus size={14} /> Novo Grupo
        </button>
      </div>

      <div className="card-soft rounded-xl p-4 flex items-start justify-between gap-3" data-testid="grupo-vinculo-frentista">
        <div className="flex items-start gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(10,42,115,0.08)" }}>
            <UserCheck size={16} className="text-[color:var(--brand-blue)]" />
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-[color:var(--ink)]">Vinculação pelo frentista no cadastro</div>
            <div className="text-[11.5px] text-neutral-500">
              {vinculoFrentista
                ? "Ligado: no Primeiro Acesso aparece \"Vincular a grupo de consumo\" com a lista dos grupos ativos."
                : "Desligado: o frentista não vê a opção. Só o Master vincula (Clientes › Editar)."}
            </div>
          </div>
        </div>
        <Switch data-testid="grupo-vinculo-switch" checked={vinculoFrentista} disabled={savingFlag}
                onCheckedChange={toggleVinculo} />
      </div>

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Grupo</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Categoria</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Regra de desconto</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Desconto do grupo</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Clientes vinculados</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Ativo</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {grupos.map((g) => (
              <TableRow key={g.id} data-testid={`grupo-row-${g.id}`}>
                <TableCell className="font-medium">{g.nome}</TableCell>
                <TableCell>
                  {g.categoria === "app_driver"
                    ? <span className="chip text-[10px] py-0.5" style={{ background: "rgba(217,119,6,0.18)", color: "#92400E" }}><Car size={11} />Motorista de App</span>
                    : <span className="chip text-[10px] py-0.5">Cliente Normal</span>}
                </TableCell>
                <TableCell className="text-[12px]" title={REGRAS_GRUPO[g.regra_desconto]?.desc}>{REGRAS_GRUPO[g.regra_desconto]?.label || "—"}</TableCell>
                <TableCell className="text-[12px]">{descricaoDescontoGrupo(g)}</TableCell>
                <TableCell>
                  {g.clientes_vinculados > 0 ? (
                    <Link to={`/master/clientes?grupo=${g.id}`} data-testid={`grupo-clientes-${g.id}`}
                          className="text-[12px] font-semibold text-[color:var(--brand-blue)] hover:underline">
                      {g.clientes_vinculados} cliente{g.clientes_vinculados > 1 ? "s" : ""}
                    </Link>
                  ) : <span className="text-[12px] text-neutral-400">nenhum</span>}
                </TableCell>
                <TableCell>
                  <Switch data-testid={`grupo-ativo-${g.id}`} checked={!!g.ativo} onCheckedChange={() => toggleAtivo(g)} />
                </TableCell>
                <TableCell className="text-right space-x-1 whitespace-nowrap">
                  <button data-testid={`grupo-edit-${g.id}`} title="Editar" onClick={() => { setEditing(g); setOpen(true); }}
                          className="icon-action icon-action--edit"><Pencil size={14} /></button>
                  <button data-testid={`grupo-del-${g.id}`} title="Excluir" onClick={() => del(g)}
                          className="icon-action icon-action--danger"><Trash2 size={14} /></button>
                </TableCell>
              </TableRow>
            ))}
            {grupos.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-6">Nenhum grupo de consumo cadastrado.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl border-none p-0">
          <div className="bg-white p-5 rounded-2xl">
            <GrupoForm key={editing?.id ?? "novo"} grupo={editing} onDone={() => { setOpen(false); load(); }} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GrupoForm({ grupo, onDone }) {
  const [nome, setNome] = useState(grupo?.nome || "");
  const [categoria, setCategoria] = useState(grupo?.categoria || "normal");
  const [regra, setRegra] = useState(grupo?.regra_desconto || "somente_grupo");
  const [tipo, setTipo] = useState(grupo?.tipo_desconto || "percentual");
  const [valor, setValor] = useState(grupo?.valor != null ? String(grupo.valor).replace(".", ",") : "");
  const [ativo, setAtivo] = useState(grupo ? !!grupo.ativo : true);
  const [saving, setSaving] = useState(false);
  const temDesconto = regra !== "somente_sistema";

  const submit = async () => {
    const v = parseFloat(String(valor).replace(",", "."));
    if (!nome.trim()) return modal.error("Informe o nome do grupo");
    if (temDesconto && (!v || v <= 0)) return modal.error("Informe o valor do desconto do grupo");
    if (temDesconto && tipo === "percentual" && v >= 100) return modal.error("O percentual deve ser menor que 100%");
    setSaving(true);
    try {
      const body = { nome: nome.trim(), categoria, regra_desconto: regra, ativo,
                     tipo_desconto: temDesconto ? tipo : null, valor: temDesconto ? v : null };
      if (grupo) await masterApi.put(`/grupos-consumo/${grupo.id}`, body);
      else await masterApi.post("/grupos-consumo", body);
      modal.success(grupo ? "Grupo atualizado" : "Grupo criado");
      onDone();
    } catch (e) { modal.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <DialogHeader>
        <DialogTitle className="font-serif-display text-xl flex items-center gap-2">
          <Users2 size={17} className="text-[color:var(--brand-blue)]" /> {grupo ? "Editar Grupo" : "Novo Grupo de Consumo"}
        </DialogTitle>
      </DialogHeader>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Nome do grupo</label>
        <input data-testid="grupo-nome" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={80}
               placeholder="Ex.: Frota Transportadora X" className={inputCls + " mt-1"} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Categoria (o grupo é subgrupo de)</label>
        <select data-testid="grupo-categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)} className={inputCls + " mt-1"}>
          <option value="normal">Cliente Normal</option>
          <option value="app_driver">Motorista de App</option>
        </select>
        <p className="text-[11px] text-neutral-500 mt-1">Só clientes desta categoria podem ser vinculados ao grupo.</p>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Regra de desconto</label>
        <div className="mt-1 space-y-1.5" data-testid="grupo-regra">
          {Object.entries(REGRAS_GRUPO).map(([k, r]) => (
            <label key={k} className={`flex items-start gap-2 cursor-pointer rounded-lg border p-2.5 transition-colors ${regra === k ? "border-[color:var(--brand-blue)] bg-blue-50/60" : "border-neutral-200 bg-white hover:border-neutral-300"}`}>
              <input type="radio" name="grupo-regra" value={k} checked={regra === k} onChange={() => setRegra(k)}
                     data-testid={`grupo-regra-${k}`} className="mt-0.5 accent-[color:var(--brand-blue)]" />
              <span className="min-w-0">
                <span className="block text-[12px] font-semibold text-[color:var(--ink)]">{r.label}</span>
                <span className="block text-[11px] text-neutral-500 leading-snug">{r.desc}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
      {temDesconto && (<>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Tipo de desconto</label>
        <select data-testid="grupo-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)} className={inputCls + " mt-1"}>
          <option value="percentual">Percentual (% no preço do litro)</option>
          <option value="litro">Por litro (centavos por litro)</option>
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">
          {tipo === "percentual" ? "Desconto (%)" : "Desconto (centavos por litro)"}
        </label>
        <input data-testid="grupo-valor" value={valor} inputMode="decimal"
               onChange={(e) => setValor(e.target.value.replace(/[^0-9.,]/g, ""))}
               placeholder={tipo === "percentual" ? "Ex.: 5" : "Ex.: 10 (= R$ 0,10 por litro)"}
               className={inputCls + " mt-1"} />
        <p className="text-[11px] text-neutral-500 mt-1">
          {tipo === "percentual"
            ? "Aplicado sobre o preço de cadastro do combustível escolhido no atendimento."
            : "Mesmo padrão dos demais descontos do sistema: 10 = R$ 0,10 de desconto em cada litro."}
        </p>
      </div>
      </>)}
      <label className="flex items-center gap-2 cursor-pointer text-[12px] text-neutral-700">
        <input type="checkbox" data-testid="grupo-ativo-check" checked={ativo} onChange={(e) => setAtivo(e.target.checked)}
               className="w-4 h-4 accent-[color:var(--brand-blue)]" />
        Grupo ativo (o desconto vale para os clientes vinculados)
      </label>
      <button data-testid="grupo-submit" onClick={submit} disabled={saving} className="btn-action btn-salvar w-full">
        <Save size={15} /> {saving ? "Salvando..." : (grupo ? "Salvar" : "Criar Grupo")}
      </button>
    </div>
  );
}
